import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ASSIGNABLE_ROLES,
  MEMBERSHIP_ROLE_MAP,
  MEMBERSHIP_STATUS_MAP,
  type AccessProfileView,
  type MembershipRole,
  type MembershipStatus,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Menu, type MenuAction } from '../../components/Menu';
import { Avatar } from '../../components/People';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  TableSkeleton,
} from '../../components/ui';

/** ما يعود به الخادم بعد دعوةٍ أو إعادة تعيين: رابطٌ أو كلمةٌ مؤقّتة أو لا شيء. */
interface Handoff {
  email: string;
  link?: string | null;
  tempPassword?: string | null;
  emailed?: boolean;
  title?: string;
}

/** ملفّاتُ الصلاحيات — للاختيار في الدعوة والتعديل. */
function useProfiles() {
  return useQuery({
    queryKey: ['team', 'profiles'],
    queryFn: () => api<AccessProfileView[]>('/team/profiles'),
    staleTime: 60_000,
  });
}

function ProfileSelect({
  id,
  value,
  onChange,
  role,
}: {
  id: string;
  value: string | null;
  onChange: (v: string | null) => void;
  /** يُظهر ملفّاتِ هذا الدورِ وحدَها — الملفُّ مبنيٌّ على دورٍ أساسيّ. */
  role: MembershipRole;
}) {
  const profiles = useProfiles();
  const matching = (profiles.data ?? []).filter((p) => p.baseRole === role);

  /* تغييرُ الدورِ يُلغي ملفًّا لا يخصّه — فلا يبقى اختيارٌ لا يطابق الدور. */
  useEffect(() => {
    if (value && profiles.data && !matching.some((p) => p.id === value)) onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, profiles.data]);

  return (
    <div className="field span-2">
      <label className="label" htmlFor={id}>
        ملفّ الصلاحيات
      </label>
      <select
        id={id}
        className="select"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">— الدور وحده (بلا تضييق) —</option>
        {matching.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <span className="tiny mute">
        {matching.length === 0
          ? `لا ملفّات صلاحيات لدور «${MEMBERSHIP_ROLE_MAP[role].label}» بعد. `
          : 'الملفُّ يضيّق ما يتيحه الدور وحدةً وحدة. '}
        <Link to="/settings/roles" className="link">
          إدارة الملفّات
        </Link>
      </span>
    </div>
  );
}

interface Member {
  id: string;
  userId: string;
  role: MembershipRole;
  status: MembershipStatus;
  name: string;
  email: string;
  phone: string | null;
  lastLoginAt: string | null;
  jobTitle: string | null;
  department: string | null;
  invitationExpiresAt: string | null;
  invitationExpired: boolean;
  profileId: string | null;
  profileName: string | null;
}

interface Seats {
  used: number;
  limit: number;
  remaining: number | null;
}

/**
 * فريق الشركة.
 *
 * كانت الشركة تُهيَّأ بمالك واحد ولا سبيل لضم أحد بعده — وهو ما جعل النظام
 * غير قابل للتسليم لشركة حقيقية مهما اكتملت بقية وحداته.
 */
export function Users() {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const [removing, setRemoving] = useState<Member | null>(null);
  const [transferring, setTransferring] = useState<Member | null>(null);
  const [invited, setInvited] = useState<Handoff | null>(null);
  const [resetting, setResetting] = useState<Member | null>(null);
  /* بحثٌ محلّيٌّ: القائمةُ كاملةٌ في اليدِ أصلًا فلا داعيَ لرحلةٍ إلى الخادم. */
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  /*
   * ارتفاعُ الجدولِ يُقاسُ لا يُخمَّن.
   *
   * التمريرُ داخلَ الجدولِ لا في الصفحة، فيأخذُ ما بقي من ارتفاعِ منطقةِ
   * المحتوى بعد الترويسةِ الثابتة. وترويسةُ الصفحةِ يتغيّرُ ارتفاعُها (بطاقاتُ
   * المقاعدِ تظهرُ وتختفي، والسطورُ تنكسرُ على الشاشاتِ الضيّقة)، فيُعادُ
   * القياسُ عند كلِّ تغيُّرٍ في حجمِها أو حجمِ النافذة.
   */
  const pageRef = useRef<HTMLDivElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const useTableHeight = (rows: number) =>
    useEffect(() => {
      const page = pageRef.current;
      const sticky = stickyRef.current;
      if (!page || !sticky) return;
      const main = page.closest('.main');
      const update = () => {
        /* الترقيمُ يقفُ أسفلَ الجدولِ داخلَ البطاقة، فيُقتطَعُ ارتفاعُه أيضًا. */
        const pager = page.querySelector<HTMLElement>('.pager');
        const avail =
          (main?.clientHeight ?? window.innerHeight) -
          sticky.offsetHeight -
          (pager?.offsetHeight ?? 0) -
          24;
        page.style.setProperty('--tbl-max', `${Math.max(220, Math.round(avail))}px`);
      };
      update();
      const ro = new ResizeObserver(update);
      ro.observe(sticky);
      if (main) ro.observe(main);
      return () => ro.disconnect();
      /* يُعادُ القياسُ حين يظهرُ الترقيمُ أو يتغيّرُ عددُ الصفوف. */
    }, [rows]);

  const members = useQuery({
    queryKey: ['team', 'members'],
    queryFn: () => api<Member[]>('/team/members'),
  });

  const seats = useQuery({
    queryKey: ['team', 'seats'],
    queryFn: () => api<Seats>('/team/seats'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['team'] });
    setEditing(null);
    setRemoving(null);
    setTransferring(null);
  };

  const isOwner = session?.user.role === 'OWNER';
  const full = seats.data ? seats.data.limit !== -1 && seats.data.remaining === 0 : false;

  /* يطابقُ الاسمَ والبريدَ والمسمّى ومسمّى الدورِ وملفَّ الصلاحيات. */
  const term = q.trim().toLowerCase();
  const shown = (members.data ?? []).filter((m) =>
    !term
      ? true
      : [
          m.name,
          m.email,
          m.jobTitle ?? '',
          m.profileName ?? '',
          MEMBERSHIP_ROLE_MAP[m.role]?.label ?? '',
          MEMBERSHIP_STATUS_MAP[m.status]?.label ?? '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(term),
  );

  /* ترقيمٌ محلّيٌّ: القائمةُ كاملةٌ في اليد، فالتقطيعُ هنا بلا رحلةٍ للخادم. */
  const pageCount = Math.max(1, Math.ceil(shown.length / pageSize));
  const paged = shown.slice((page - 1) * pageSize, page * pageSize);
  /* البحثُ يعيدُ إلى الأولى، والصفحةُ لا تتجاوزُ العدد بعد تغيُّرِ الفلتر. */
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);
  useTableHeight(paged.length);

  return (
    <div className="page" ref={pageRef}>
      {/* الترويسةُ والبطاقاتُ ثابتةٌ — يبدأُ التمريرُ من الجدولِ تحتها. */}
      <div className="page-sticky" ref={stickyRef}>
      <PageHead
        title="المستخدمون"
        subtitle="من يدخل النظام، وبأي صلاحية"
        actions={
          <>
            {/* البحثُ في الترويسةِ لا في صفٍّ مستقلٍّ — توفيرًا للارتفاع. */}
            <div className="hd-search">
              <Icon name="search" size={14} />
              <input
                className="hd-search-input"
                placeholder="ابحث بالاسم أو البريد أو الدور…"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
              />
              {q && (
                <button
                  type="button"
                  className="hd-search-clear"
                  onClick={() => setQ('')}
                  aria-label="مسح البحث"
                >
                  <Icon name="x" size={13} />
                </button>
              )}
            </div>
            <Link to="/settings/roles" className="btn">
              <Icon name="layers" size={15} />
              ملفّات الصلاحيات
            </Link>
            <button
              className="btn btn-primary"
              onClick={() => setInviting(true)}
              disabled={full}
              title={full ? 'اكتمل عدد المقاعد في باقتك' : undefined}
            >
              <Icon name="plus" size={15} />
              إضافة مستخدم
            </button>
          </>
        }
      />

      {seats.data && (
        <div className="user-stats">
          <div className="ustat is-brand">
            <span className="ustat-ic">
              <Icon name="users" size={22} />
            </span>
            <span className="ustat-body">
              <strong className="num">{seats.data.used}</strong>
              <span>المستخدمون النشطون</span>
            </span>
          </div>
          <div className="ustat is-gold">
            <span className="ustat-ic">
              <Icon name="diamond" size={20} />
            </span>
            <span className="ustat-body">
              <strong className="num">{seats.data.limit === -1 ? '∞' : seats.data.limit}</strong>
              <span>حدّ الباقة</span>
            </span>
          </div>
          <div className="ustat is-accent">
            <span className="ustat-ic">
              <Icon name="check" size={22} />
            </span>
            <span className="ustat-body">
              <strong className="num">
                {seats.data.remaining === null ? '∞' : seats.data.remaining}
              </strong>
              <span>المقاعد المتبقّية</span>
            </span>
          </div>
        </div>
      )}
      </div>

      {full && (
        <div className="alert alert-warning mb-4">
          اكتمل عدد المقاعد في باقتك. رقِّ الباقة أو أوقف عضوًا قائمًا لتحرير مقعد — والعضو الموقوف
          لا يشغل مقعدًا.
        </div>
      )}

      {members.isError && <ErrorState error={members.error} onRetry={() => members.refetch()} />}

      <div className="card">
        <div className="table-wrap tbl-scroll">
          <table className="table">
            <thead>
              <tr>
                {/* الإجراءاتُ أوّلَ الصفِّ (يمينَه في العربيّة) — قريبةٌ من اسمِ العضو. */}
                <th className="u-actions-col" />
                <th>العضو</th>
                <th>الدور</th>
                <th>ملفّ الصلاحيات</th>
                <th>الحالة</th>
                <th>آخر دخول</th>
              </tr>
            </thead>
            {members.isLoading && <TableSkeleton rows={4} cols={6} />}

            <tbody>
              {paged.map((m) => {
                const isSelf = m.userId === session?.user.id;
                const canManage = m.role !== 'OWNER' && !isSelf;

                return (
                  <tr key={m.id}>
                    <td className="u-actions-col">
                      <RowActions
                        member={m}
                        isOwner={isOwner}
                        canManage={canManage}
                        onEdit={() => setEditing(m)}
                        onReset={() => setResetting(m)}
                        onTransfer={() => setTransferring(m)}
                        onRemove={() => setRemoving(m)}
                        onResent={setInvited}
                      />
                    </td>
                    <td>
                      <span className="row" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
                        <Avatar name={m.name} size={34} />
                        <span className="col" style={{ gap: 0, minWidth: 0 }}>
                          <span className="cell-strong">{m.name}</span>
                          <span className="tiny mute num" dir="ltr">
                            {m.email}
                          </span>
                          {m.jobTitle && <span className="tiny mute">{m.jobTitle}</span>}
                        </span>
                      </span>
                    </td>
                    <td>
                      <Badge kind={MEMBERSHIP_ROLE_MAP[m.role].kind}>
                        {MEMBERSHIP_ROLE_MAP[m.role].label}
                      </Badge>
                    </td>
                    <td>
                      {m.profileName ? (
                        <span className="row" style={{ gap: 5, alignItems: 'center' }}>
                          <Icon name="layers" size={14} />
                          {m.profileName}
                        </span>
                      ) : (
                        <span className="tiny mute">الدور وحده (بلا تضييق)</span>
                      )}
                    </td>
                    <td>
                      <Badge kind={MEMBERSHIP_STATUS_MAP[m.status].kind}>
                        {MEMBERSHIP_STATUS_MAP[m.status].label}
                      </Badge>
                      {m.status === 'INVITED' && m.invitationExpired && (
                        <div className="tiny neg">انتهت صلاحية الدعوة</div>
                      )}
                    </td>
                    <td className="num cell-mute">
                      {m.lastLoginAt ? dateTime(m.lastLoginAt) : 'لم يدخل بعد'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {members.data?.length === 0 && (
          <EmptyState title="لا أعضاء بعد" text="ادعُ زملاءك ليعملوا معك على النظام." />
        )}
        {(members.data?.length ?? 0) > 0 && shown.length === 0 && (
          <EmptyState title="لا نتيجة" text={`لا عضوَ يطابقُ «${q}».`} />
        )}

        <Pager
          page={page}
          pageCount={pageCount}
          total={shown.length}
          pageSize={pageSize}
          onPage={setPage}
          onPageSize={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      </div>

      {inviting && (
        <InviteDialog
          onClose={() => setInviting(false)}
          onDone={(result) => {
            setInviting(false);
            setInvited(result);
            void queryClient.invalidateQueries({ queryKey: ['team'] });
          }}
        />
      )}

      {invited && <InviteLinkDialog invited={invited} onClose={() => setInvited(null)} />}

      {resetting && (
        <ResetPasswordDialog
          member={resetting}
          onClose={() => setResetting(null)}
          onDone={(r) => {
            setResetting(null);
            setInvited(r);
          }}
        />
      )}

      {editing && <EditDialog member={editing} onClose={() => setEditing(null)} onDone={refresh} />}

      {removing && (
        <RemoveDialog member={removing} onClose={() => setRemoving(null)} onDone={refresh} />
      )}

      {transferring && (
        <TransferDialog
          member={transferring}
          onClose={() => setTransferring(null)}
          onDone={() => {
            refresh();
            // الدور تغيّر: الجلسة تحمل الدور القديم حتى تُحدَّث.
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

/* ==========================================================================
   الدعوة
   ========================================================================== */

/** موظفٌ له ملفٌ في الموارد البشريةِ ولا حسابَ بعد — يُنتقى فتُملأ بياناتُه. */
interface CandidateEmployee {
  id: string;
  name: string;
  email: string | null;
  jobTitle: string | null;
  department: string | null;
}

/**
 * إنشاءُ مستخدم — من ملفِّ موظفٍ قائمٍ أو من الصفر.
 *
 * أكثرُ من يُنشأ له حسابٌ موظفٌ له ملفٌ في الموارد البشرية أصلًا: فبدل
 * إعادةِ كتابةِ اسمِه وبريدِه ثم تركِ ملفين لا يتطابقان، يُنتقى ملفُّه
 * فتُملأ بياناتُه ويُربط الحسابُ به. ومن لا ملفَّ له تُدخَل بياناتُه يدويًّا.
 */
function InviteDialog({
  onClose,
  onDone,
}: {
  onClose: () => void;
  onDone: (result: Handoff) => void;
}) {
  const candidates = useQuery({
    queryKey: ['team', 'candidate-employees'],
    queryFn: () => api<CandidateEmployee[]>('/team/candidate-employees'),
  });

  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [employeeId, setEmployeeId] = useState('');
  const [email, setEmail] = useState('');
  /* الاسمُ في النظامِ اسمان دائمًا: أوّلٌ وثانٍ — يُجمعان اسمًا كاملًا. */
  const [firstName, setFirstName] = useState('');
  const [secondName, setSecondName] = useState('');
  const [role, setRole] = useState<MembershipRole>('EMPLOYEE');
  const [profileId, setProfileId] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<'TEMP_PASSWORD' | 'LINK'>('TEMP_PASSWORD');
  /* في وضعِ «بلا ملف» وحدَه: خيارُ إنشاءِ ملفِّ موظفٍ مع الحساب. */
  const [createEmployee, setCreateEmployee] = useState(false);
  const [jobTitle, setJobTitle] = useState('');

  const chosen = candidates.data?.find((e) => e.id === employeeId) ?? null;

  /* الاسمُ الكاملُ من الجزأين — يُرسل إلى الخادم كما اعتاد. */
  const name = [firstName.trim(), secondName.trim()].filter(Boolean).join(' ');

  /* اختيارُ موظفٍ يملأ اسمَه (مقسومًا) وبريدَه من ملفِّه. */
  const pickEmployee = (id: string) => {
    setEmployeeId(id);
    const emp = candidates.data?.find((e) => e.id === id);
    if (emp) {
      const parts = emp.name.trim().split(/\s+/);
      setFirstName(parts[0] ?? '');
      setSecondName(parts.slice(1).join(' '));
      setEmail(emp.email ?? '');
    }
  };

  const invite = useMutation({
    mutationFn: () =>
      api<{
        email: string;
        link: string | null;
        tempPassword: string | null;
        isNewAccount: boolean;
        emailed: boolean;
      }>('/team/members', {
        method: 'POST',
        body: {
          email,
          name,
          role,
          profileId,
          delivery,
          /* الملفُّ في «بلا ملف» خيارٌ، وفي «له ملف» يُربط القائمُ لا يُنشأ. */
          createEmployee: mode === 'new' ? createEmployee : false,
          ...(mode === 'new' && createEmployee && jobTitle.trim() ? { jobTitle } : {}),
          ...(mode === 'existing' && employeeId ? { employeeId } : {}),
        },
      }),
    onSuccess: (data) =>
      onDone({
        email: data.email,
        link: data.link,
        tempPassword: data.tempPassword,
        emailed: data.emailed,
      }),
  });

  const valid =
    email.includes('@') &&
    firstName.trim().length >= 1 &&
    secondName.trim().length >= 1 &&
    name.length >= 2 &&
    (mode === 'new' || !!employeeId);

  return (
    <Modal
      title="إضافة مستخدم"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => invite.mutate()}
            disabled={!valid || invite.isPending}
          >
            {invite.isPending ? 'جارٍ الإضافة' : 'إضافة'}
          </button>
        </>
      }
    >
      {invite.isError && (
        <div className="alert alert-danger mb-4">
          {invite.error instanceof Error ? invite.error.message : 'تعذّرت الدعوة.'}
        </div>
      )}

      <div className="form-grid">
        {/* النوع أولًا — بطاقتان تُنقران: ملفٌ يُنتقى، أو بياناتٌ تُدخَل. */}
        <div className="field span-2">
          <span className="label">نوع المستخدم</span>
          <div className="user-type">
            <button
              type="button"
              className={`user-type-opt${mode === 'existing' ? ' is-on' : ''}`}
              onClick={() => setMode('existing')}
              aria-pressed={mode === 'existing'}
            >
              <span className="user-type-ic">
                <Icon name="briefcase" size={20} />
              </span>
              <span className="user-type-txt">
                <strong>موظف له ملف</strong>
                <span>اختر اسمه من الموارد البشرية، فتُملأ بياناته تلقائيًّا ويُربط الحساب بملفّه.</span>
              </span>
              <span className="user-type-tick" aria-hidden>
                <Icon name="check" size={14} />
              </span>
            </button>

            <button
              type="button"
              className={`user-type-opt${mode === 'new' ? ' is-on' : ''}`}
              onClick={() => {
                setMode('new');
                setEmployeeId('');
              }}
              aria-pressed={mode === 'new'}
            >
              <span className="user-type-ic">
                <Icon name="user" size={20} />
              </span>
              <span className="user-type-txt">
                <strong>موظف بلا ملف</strong>
                <span>أدخل بياناته كاملةً يدويًّا — مع خيار إنشاء ملفّ له في الموارد البشرية.</span>
              </span>
              <span className="user-type-tick" aria-hidden>
                <Icon name="check" size={14} />
              </span>
            </button>
          </div>
        </div>

        {mode === 'existing' && (
          <div className="field span-2">
            <label className="label req" htmlFor="i-emp">
              الموظف
            </label>
            {candidates.isPending ? (
              <div className="skeleton" style={{ height: 38 }} />
            ) : (candidates.data?.length ?? 0) === 0 ? (
              <p className="tiny mute">
                لا موظفين بلا حساب في الموارد البشرية. اختر «موظف بلا ملف» لإدخال البيانات يدويًّا.
              </p>
            ) : (
              <select
                id="i-emp"
                className="select"
                value={employeeId}
                onChange={(e) => pickEmployee(e.target.value)}
              >
                <option value="">اختر الموظف…</option>
                {candidates.data!.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                    {e.jobTitle ? ` — ${e.jobTitle}` : ''}
                    {e.department ? ` (${e.department})` : ''}
                  </option>
                ))}
              </select>
            )}
            {chosen && (
              <span className="tiny mute">تُملأ بياناتُه من ملفِّه، ويُربط الحسابُ الجديدُ به.</span>
            )}
          </div>
        )}

        <div className="field span-2">
          <label className="label req" htmlFor="i-email">
            البريد الإلكتروني
          </label>
          <input
            id="i-email"
            className="input num"
            dir="ltr"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <span className="tiny mute">
            {mode === 'existing' && chosen && !chosen.email
              ? 'لا بريد في ملفِّه — أدخله ليصله الحساب.'
              : 'إليه تصل الدعوة، وبه يسجّل الدخول. لا يُغيَّر بعد الإنشاء.'}
          </span>
        </div>

        {/* الاسمُ اسمان دائمًا: أوّلٌ وثانٍ، جنبًا إلى جنب. */}
        <div className="field">
          <label className="label req" htmlFor="i-first">
            الاسم الأول
          </label>
          <input
            id="i-first"
            className="input"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label req" htmlFor="i-second">
            الاسم الثاني
          </label>
          <input
            id="i-second"
            className="input"
            value={secondName}
            onChange={(e) => setSecondName(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="i-role">
            الدور
          </label>
          <select
            id="i-role"
            className="select"
            value={role}
            onChange={(e) => setRole(e.target.value as MembershipRole)}
          >
            {ASSIGNABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {MEMBERSHIP_ROLE_MAP[r].label}
              </option>
            ))}
          </select>
          <span className="tiny mute">{MEMBERSHIP_ROLE_MAP[role].description}</span>
        </div>

        <ProfileSelect id="i-profile" value={profileId} onChange={setProfileId} role={role} />

        {/*
          كيف يدخل أوّلَ مرّة.
          كلمةٌ مؤقّتة تصله بالبريد ويُلزَم بتبديلها — الحسابُ جاهزٌ في دقيقة؛
          أو رابطٌ يضبط به كلمتَه بنفسه فلا تمرّ في يد أحد.
        */}
        {/* أوّلُ دخولٍ — بطاقتان تُنقران: كلمةٌ تُرسل، أو رابطٌ يضبطه بنفسه. */}
        <div className="field span-2">
          <span className="label">أول دخول</span>
          <div className="user-type">
            <button
              type="button"
              className={`user-type-opt${delivery === 'TEMP_PASSWORD' ? ' is-on' : ''}`}
              onClick={() => setDelivery('TEMP_PASSWORD')}
              aria-pressed={delivery === 'TEMP_PASSWORD'}
            >
              <span className="user-type-ic">
                <Icon name="send" size={19} />
              </span>
              <span className="user-type-txt">
                <strong>إرسال كلمة مؤقّتة</strong>
                <span>تصله بالبريد جاهزةً، ويُطلب منه تغييرها عند أول دخول.</span>
              </span>
              <span className="user-type-tick" aria-hidden>
                <Icon name="check" size={14} />
              </span>
            </button>

            <button
              type="button"
              className={`user-type-opt${delivery === 'LINK' ? ' is-on' : ''}`}
              onClick={() => setDelivery('LINK')}
              aria-pressed={delivery === 'LINK'}
            >
              <span className="user-type-ic">
                <Icon name="link" size={19} />
              </span>
              <span className="user-type-txt">
                <strong>رابط دعوة</strong>
                <span>يضبط به كلمة مروره بنفسه فلا تمرّ بأحد (صالح لسبعة أيام).</span>
              </span>
              <span className="user-type-tick" aria-hidden>
                <Icon name="check" size={14} />
              </span>
            </button>
          </div>
        </div>

        {/* الملفُّ خيارٌ في «بلا ملف» وحدَه: من له ملفٌ يُربط لا يُنشأ له ثانٍ. */}
        {mode === 'new' && (
          <div className="field span-2">
            <label className={`user-hr${createEmployee ? ' is-on' : ''}`}>
              <input
                type="checkbox"
                checked={createEmployee}
                onChange={(e) => setCreateEmployee(e.target.checked)}
              />
              <span className="user-hr-ic">
                <Icon name="briefcase" size={17} />
              </span>
              <span className="user-hr-txt">
                <strong>أنشئ له ملفًّا في الموارد البشرية</strong>
                <span>فيظهر في كشف الحضور والرواتب، ولا تفترق قائمة المستخدمين عن الموظفين.</span>
              </span>
            </label>

            {createEmployee && (
              <div className="field mt-3">
                <label className="label" htmlFor="i-title">
                  المسمّى الوظيفي
                </label>
                <input
                  id="i-title"
                  className="input"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="مثلًا: أخصائي مبيعات"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * رابط الدعوة.
 *
 * يُعرض دائمًا ولا يُكتفى بالبريد: صندوق الشركة قد لا يكون مضبوطًا، وقد
 * تسقط الرسالة في البريد المزعج. الرابط في يد من دعا يضمن وصولها.
 */
function InviteLinkDialog({ invited, onClose }: { invited: Handoff; onClose: () => void }) {
  const [copied, setCopied] = useState(false);

  return (
    <Modal
      title={invited.title ?? 'أُضيف المستخدم'}
      onClose={onClose}
      footer={
        <button className="btn btn-primary" onClick={onClose}>
          تم
        </button>
      }
    >
      {invited.tempPassword ? (
        <>
          <div className={`alert ${invited.emailed ? 'alert-info' : 'alert-warning'} mb-4`}>
            <Icon name={invited.emailed ? 'check' : 'alert'} size={15} />
            <div>
              {invited.emailed
                ? `أُرسلت كلمةُ مرورٍ مؤقّتة إلى ${invited.email} مع عنوان الدخول — وهذه هي إن تأخّر البريد أو سقط في المزعج.`
                : `لم يُرسَل بريد (لا مُرسِل مضبوطًا). سلّم ${invited.email} كلمةَ المرور المؤقّتة التالية.`}{' '}
              تظهر مرّةً واحدة، وسيُطلب منه تغييرها عند أول دخول.
            </div>
          </div>
          <div className="field">
            <span className="label">كلمة المرور المؤقّتة</span>
            <input
              className="input num"
              dir="ltr"
              readOnly
              value={invited.tempPassword}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              className="btn btn-sm mt-2"
              onClick={() => {
                void navigator.clipboard.writeText(invited.tempPassword!);
                setCopied(true);
              }}
            >
              {copied ? 'نُسخت' : 'نسخ'}
            </button>
          </div>
        </>
      ) : invited.emailed && !invited.link ? (
        <div className="alert alert-info">
          <Icon name="check" size={15} />
          <div>
            أُرسلت كلمةُ مرورٍ مؤقّتة إلى <span className="num">{invited.email}</span> مع عنوان
            الدخول. عند أوّل دخولٍ سيُطلب منه اختيارُ كلمةٍ جديدة.
          </div>
        </div>
      ) : invited.link ? (
        <>
          <div className="alert alert-info mb-4">
            <Icon name="check" size={15} />
            <div>
              أُضيف <span className="num">{invited.email}</span> إلى الفريق. سلّمه الرابط التالي
              ليضبط كلمة مروره — وهو صالح لسبعة أيام.
            </div>
          </div>

          <div className="field">
            <span className="label">رابط الدعوة</span>
            <textarea
              className="input num"
              dir="ltr"
              rows={3}
              readOnly
              value={invited.link}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              className="btn btn-sm mt-2"
              onClick={() => {
                void navigator.clipboard.writeText(invited.link!);
                setCopied(true);
              }}
            >
              {copied ? 'نُسخ' : 'نسخ الرابط'}
            </button>
          </div>
        </>
      ) : (
        <div className="alert alert-info">
          البريد <span className="num">{invited.email}</span> له حساب على المنصة بالفعل، فأُضيف إلى
          فريقك مباشرةً <span className="strong">بكلمة مروره الحالية</span>. لا رابط دعوة ولا كلمة
          مرور جديدة.
        </div>
      )}
    </Modal>
  );
}

/**
 * إجراءاتُ صفِّ العضو في قائمةٍ من ثلاثِ نقاط.
 *
 * الأزرارُ المتناثرةُ كانت تُثقل الصفَّ وتُطيله؛ فجُمِعت في قائمةٍ تُفتح
 * بنقرة — تعديلٌ وإعادةُ كلمةِ مرورٍ ونقلُ ملكيّةٍ وإزالة، وإعادةُ الدعوة
 * للمدعوّ. والمالكُ ونفسُك بلا إجراءات، فلا تُعرض القائمةُ فارغة.
 */
function RowActions({
  member,
  isOwner,
  canManage,
  onEdit,
  onReset,
  onTransfer,
  onRemove,
  onResent,
}: {
  member: Member;
  isOwner: boolean;
  canManage: boolean;
  onEdit: () => void;
  onReset: () => void;
  onTransfer: () => void;
  onRemove: () => void;
  onResent: (r: { email: string; link: string }) => void;
}) {
  const resend = useMutation({
    mutationFn: () =>
      api<{ email: string; link: string }>(`/team/members/${member.id}/resend`, {
        method: 'POST',
      }),
    onSuccess: onResent,
  });

  const actions: MenuAction[] = [];
  if (member.status === 'INVITED') {
    actions.push({
      key: 'resend',
      label: 'إعادة الدعوة',
      icon: 'send',
      onSelect: () => resend.mutate(),
    });
  }
  if (canManage) {
    actions.push({ key: 'edit', label: 'تعديل', icon: 'edit', onSelect: onEdit });
  }
  if (canManage && member.status === 'ACTIVE') {
    actions.push({
      key: 'reset',
      label: 'إعادة تعيين كلمة المرور',
      icon: 'refresh',
      title: 'كلمة مرور مؤقّتة تُرسل إليه ويُلزَم بتبديلها',
      onSelect: onReset,
    });
  }
  if (isOwner && canManage && member.status === 'ACTIVE') {
    actions.push({
      key: 'transfer',
      label: 'نقل الملكية',
      icon: 'logout',
      separated: true,
      onSelect: onTransfer,
    });
  }
  if (canManage) {
    actions.push({
      key: 'remove',
      label: 'إزالة العضو',
      icon: 'trash',
      danger: true,
      separated: true,
      onSelect: onRemove,
    });
  }

  if (actions.length === 0) return <span className="tiny mute">—</span>;
  return <Menu actions={actions} label={`إجراءات ${member.name}`} />;
}

/* ==========================================================================
   التعديل والإزالة
   ========================================================================== */

function EditDialog({
  member,
  onClose,
  onDone,
}: {
  member: Member;
  onClose: () => void;
  onDone: () => void;
}) {
  const [role, setRole] = useState<MembershipRole>(member.role);
  const [profileId, setProfileId] = useState<string | null>(member.profileId);
  const [status, setStatus] = useState(member.status === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE');

  const save = useMutation({
    mutationFn: () =>
      api(`/team/members/${member.id}`, {
        method: 'PATCH',
        body: {
          ...(role !== member.role ? { role } : {}),
          ...(profileId !== member.profileId ? { profileId } : {}),
          ...(status !== member.status && member.status !== 'INVITED' ? { status } : {}),
        },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`تعديل ${member.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor="e-role">
          الدور
        </label>
        <select
          id="e-role"
          className="select"
          value={role}
          onChange={(e) => setRole(e.target.value as MembershipRole)}
        >
          {ASSIGNABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {MEMBERSHIP_ROLE_MAP[r].label}
            </option>
          ))}
        </select>
        <span className="tiny mute">{MEMBERSHIP_ROLE_MAP[role].description}</span>
      </div>

      <div className="form-grid mt-4">
        <ProfileSelect id="e-profile" value={profileId} onChange={setProfileId} role={role} />
      </div>

      {member.status !== 'INVITED' && (
        <div className="field mt-4">
          <label className="label" htmlFor="e-status">
            الحالة
          </label>
          <select
            id="e-status"
            className="select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            <option value="ACTIVE">نشط</option>
            <option value="SUSPENDED">موقوف</option>
          </select>
          {status === 'SUSPENDED' && (
            <span className="tiny neg">
              الإيقاف يُنهي جلساته المفتوحة فورًا ويمنع دخوله. بياناته وما أسند إليه تبقى كما هي،
              ولا يشغل مقعدًا في الباقة.
            </span>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * إعادةُ تعيين كلمة المرور — كلمةٌ مؤقّتة وإغلاقُ جلساته.
 * تأكيدٌ صريح: بعدها لا يدخل بكلمته القديمة.
 */
function ResetPasswordDialog({
  member,
  onClose,
  onDone,
}: {
  member: Member;
  onClose: () => void;
  onDone: (r: Handoff) => void;
}) {
  const reset = useMutation({
    mutationFn: () =>
      api<{ email: string; emailed: boolean; tempPassword: string | null }>(
        `/team/members/${member.id}/reset-password`,
        { method: 'POST' },
      ),
    onSuccess: (r) =>
      onDone({
        email: r.email,
        emailed: r.emailed,
        tempPassword: r.tempPassword,
        title: 'أُعيد تعيين كلمة المرور',
      }),
  });

  return (
    <Modal
      title={`إعادة تعيين كلمة مرور ${member.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => reset.mutate()}
            disabled={reset.isPending}
          >
            {reset.isPending ? 'جارٍ' : 'إعادة التعيين'}
          </button>
        </>
      }
    >
      {reset.isError && (
        <div className="alert alert-danger mb-4">
          {reset.error instanceof Error ? reset.error.message : 'تعذّرت العملية.'}
        </div>
      )}
      <p>
        تُرسل كلمةُ مرورٍ مؤقّتة إلى <span className="num">{member.email}</span>، وتُغلق جلساتُه
        المفتوحة فورًا، ويُطلب منه اختيارُ كلمةٍ جديدة عند أوّل دخول. كلمتُه القديمة لا تعمل بعدها.
      </p>
    </Modal>
  );
}

function RemoveDialog({
  member,
  onClose,
  onDone,
}: {
  member: Member;
  onClose: () => void;
  onDone: () => void;
}) {
  const remove = useMutation({
    mutationFn: () => api(`/team/members/${member.id}`, { method: 'DELETE' }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="إزالة عضو"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => remove.mutate()}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'جارٍ الإزالة' : 'إزالة'}
          </button>
        </>
      }
    >
      {remove.isError && (
        <div className="alert alert-danger mb-4">
          {remove.error instanceof Error ? remove.error.message : 'تعذّرت الإزالة.'}
        </div>
      )}

      <p>
        سيفقد <span className="strong">{member.name}</span> وصوله إلى النظام فورًا.
      </p>

      {/*
        الفرق بين الإزالة والإيقاف يستحق التصريح: الإيقاف يُرجَع عنه بضغطة،
        والإزالة تتطلب دعوة جديدة وقبولًا جديدًا.
      */}
      <div className="alert alert-info mt-4">
        ملف الموظف وحضوره ومرتباته وما أُسند إليه من عملاء وصفقات تبقى كما هي — تُحذف عضويته لا
        تاريخه. وإن كان الأمر مؤقتًا فالإيقاف أنسب: يُرجَع عنه بضغطة ولا يحتاج دعوة جديدة.
      </div>
    </Modal>
  );
}

/**
 * نقل الملكية.
 *
 * آخر عملية يملكها المالك: بعدها لا يستطيع استعادتها بنفسه، ولذلك تُطلب
 * كلمة مروره لا مجرد تأكيد بضغطة.
 */
function TransferDialog({
  member,
  onClose,
  onDone,
}: {
  member: Member;
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const transfer = useMutation({
    mutationFn: () =>
      api('/team/transfer-ownership', {
        method: 'POST',
        body: { membershipId: member.id, password },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="نقل ملكية الحساب"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => transfer.mutate()}
            disabled={!confirmed || password.length === 0 || transfer.isPending}
          >
            {transfer.isPending ? 'جارٍ النقل' : 'نقل الملكية'}
          </button>
        </>
      }
    >
      {transfer.isError && (
        <div className="alert alert-danger mb-4">
          {transfer.error instanceof Error ? transfer.error.message : 'تعذّر النقل.'}
        </div>
      )}

      <div className="alert alert-warning mb-4">
        <span className="strong">هذه العملية لا رجعة فيها من طرفك.</span> سيصير{' '}
        <span className="strong">{member.name}</span> مالك الحساب، وتصير أنت مدير نظام. استعادة
        الملكية بعدها تحتاج موافقته هو.
      </div>

      <div className="field">
        <label className="label req" htmlFor="t-password">
          كلمة مرورك للتأكيد
        </label>
        <input
          id="t-password"
          className="input"
          type="password"
          dir="ltr"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>

      <label className="check mt-4">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>أفهم أنني سأفقد ملكية الحساب</span>
      </label>
    </Modal>
  );
}
